import { ArrowRight, Bot, Sparkles, Palette, Phone, Users, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import Footer from "@/components/layout/Footer";

const Index = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <header className="container mx-auto px-6 py-4">
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Bot className="h-6 w-6 text-primary" />
            <span className="font-bold text-xl">OfferChat</span>
          </div>
          <div className="flex items-center gap-6">
            <Link to="/docs" className="text-gray-600 hover:text-gray-900 font-medium">
              Documentation
            </Link>
            <Link to="/portal/auth" className="text-primary hover:text-primary/80 font-medium">
              Login
            </Link>
          </div>
        </div>
      </header>

      <main className="container px-6 mx-auto">
        {/* Hero Section */}
        <section className="pt-20 pb-32 text-center fade-in">
          <div className="max-w-3xl mx-auto space-y-8">
            <h1 className="text-5xl font-bold tracking-tight sm:text-6xl text-gray-900">
              Transform Customer Engagement with OfferChat
            </h1>
            <p className="text-xl text-gray-600 mt-6">
              Seamlessly integrate AI chatbots with your CRM, handle outbound calls, and connect customers to live representatives. The complete solution for modern customer engagement.
            </p>
            <div className="flex items-center justify-center gap-4 mt-8">
              <Button size="lg" className="hover-scale">
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button variant="outline" size="lg" className="hover-scale">
                Schedule Demo
              </Button>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Bot className="h-8 w-8 text-theme-teal" />}
              title="AI-Powered Chatbots"
              description="Custom chatbot solutions with advanced natural language processing and CRM integration capabilities."
            />
            <FeatureCard
              icon={<Phone className="h-8 w-8 text-theme-teal" />}
              title="Outbound Call Management"
              description="Automated outbound call handling with scheduling and compliance measures built-in."
            />
            <FeatureCard
              icon={<Users className="h-8 w-8 text-theme-teal" />}
              title="Live Rep Connection"
              description="Seamless transition from chatbot to live representative when needed, with smart routing."
            />
          </div>
        </section>

        {/* Integration Section */}
        <section className="py-24 border-t">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Powerful CRM Integration</h2>
            <p className="text-xl text-gray-600">Connect with your favorite CRM platforms</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Sparkles className="h-8 w-8 text-theme-teal" />}
              title="Multiple CRM Support"
              description="Integrate with Salesforce, HubSpot, Zoho, and more with our easy setup process."
            />
            <FeatureCard
              icon={<Shield className="h-8 w-8 text-theme-teal" />}
              title="Security & Compliance"
              description="Enterprise-grade security with GDPR, CCPA, and HIPAA compliance support."
            />
            <FeatureCard
              icon={<Palette className="h-8 w-8 text-theme-teal" />}
              title="Customization"
              description="Tailor the chatbot experience to match your brand and specific needs."
            />
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

const FeatureCard = ({ icon, title, description }: { icon: React.ReactNode; title: string; description: string }) => {
  return (
    <div className="glass-panel rounded-xl p-6 hover-scale">
      <div className="space-y-4">
        <div className="inline-block p-3 rounded-lg bg-gray-100">{icon}</div>
        <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
        <p className="text-gray-600">{description}</p>
      </div>
    </div>
  );
};

export default Index;
